/**
 * @fileoverview Review & Analysis sub-agent route
 *
 * Renders the live AgentDetailRouter for the entry's underlying manifest.
 * Slug → metadata is resolved through the shared `reviewRegistry`.
 *
 * URL: /dashboard/jobs/:jobId/review/:agent
 *   - ?head=<headKey> opens straight to that head inside the dedicated
 *     workspaces, so one agent can link to a specific head in another
 *     (e.g. the s588G duty cross-link between the two insolvent-trading heads)
 *   - ?granularity=&period=&indicator= carry the financial-analysis context
 *     between Historical Financial Analysis and Solvency Analysis, and
 *     across a reload (MOR-1280)
 *
 * Unknown slugs short-circuit to a notFound() response. Auth is enforced
 * by the parent route (`_layout.jobs.$jobId.review.tsx`).
 */

import { useEffect, useMemo } from "react";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { parseFinancialContext } from "@/components/jobs/phases/gather/summarization/financialAnalysis/financialContext";
import type { FinancialAnalysisContext } from "@/components/jobs/phases/gather/summarization/financialAnalysis/types";
import { AgentDetailRouter } from "@/components/jobs/phases/gather/summarization/AgentDetailRouter";
import { FlaggedTransactionsWorkspace } from "@/components/jobs/phases/review/flaggedTransactions/FlaggedTransactionsWorkspace";
import { CivilCriminalOffencesWorkspace } from "@/components/jobs/phases/review/civilCriminalOffences/CivilCriminalOffencesWorkspace";
import { BooksAndRecordsView } from "@/components/jobs/phases/gather/booksAndRecords/BooksAndRecordsView";
import {
  REVIEW_AGENTS_BY_SLUG,
  isReviewAgentVisibleOnJob,
  reviewSlugForFinancialSection,
  type ReviewAgentEntry,
} from "@/components/jobs/phases/review/reviewRegistry";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/review/$agent",
)({
  beforeLoad: ({ params }) => {
    if (!REVIEW_AGENTS_BY_SLUG.has(params.agent)) {
      throw notFound();
    }
  },
  validateSearch: (
    search: Record<string, unknown>,
  ): { head?: string } & FinancialAnalysisContext => ({
    head: typeof search.head === "string" ? search.head : undefined,
    ...parseFinancialContext(search),
  }),
  component: ReviewAgentPage,
});

/**
 * Route component: dispatches to the workspace for the `agent` param and
 * passes the financial-analysis context parsed from the search params.
 */
function ReviewAgentPage() {
  const { jobId, agent } = Route.useParams();
  const { head, ...financialContext } = Route.useSearch();
  const navigate = useNavigate();
  const meta = REVIEW_AGENTS_BY_SLUG.get(agent);

  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );

  // Agents hidden on this job's regime (e.g. Civil & Criminal Offences on a
  // bankruptcy job, whose Bankruptcy Act offence heads aren't modelled yet)
  // bounce to the Investigations overview so a typed URL / stale bookmark
  // can't reach the deferred workspace.
  const hidden =
    !!job && !isReviewAgentVisibleOnJob(agent, job.appointmentType);
  useEffect(() => {
    if (!hidden) return;
    void navigate({
      to: "/dashboard/jobs/$jobId/review",
      params: { jobId },
      replace: true,
    });
  }, [hidden, jobId, navigate]);

  if (!meta || hidden) return null;

  // Dedicated workspaces bypass the generic AgentDetailRouter dispatch.
  if (meta.customView) {
    if (meta.dataAgentId === "sum_offence_master") {
      return <OffenceWorkspacePage jobId={jobId} initialHead={head} />;
    }
    if (meta.dataAgentId === "sum_books_and_records") {
      return <BooksAndRecordsView jobId={jobId as Id<"jobs">} />;
    }
    return <FlaggedWorkspacePage jobId={jobId} initialHead={head} />;
  }

  return (
    <StandardReviewAgentPage
      jobId={jobId}
      meta={meta}
      financialContext={financialContext}
    />
  );
}

function OffenceWorkspacePage({
  jobId,
  initialHead,
}: {
  jobId: string;
  initialHead?: string;
}) {
  const navigate = useNavigate();
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );
  return (
    <CivilCriminalOffencesWorkspace
      jobId={jobId as Id<"jobs">}
      isViewer={job?.userRole === "viewer"}
      initialHead={initialHead}
      onBack={() =>
        navigate({ to: "/dashboard/jobs/$jobId/review", params: { jobId } })
      }
    />
  );
}

function FlaggedWorkspacePage({
  jobId,
  initialHead,
}: {
  jobId: string;
  initialHead?: string;
}) {
  const navigate = useNavigate();
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );
  return (
    <FlaggedTransactionsWorkspace
      jobId={jobId as Id<"jobs">}
      isViewer={job?.userRole === "viewer"}
      initialHead={initialHead}
      onBack={() =>
        navigate({ to: "/dashboard/jobs/$jobId/review", params: { jobId } })
      }
    />
  );
}

/**
 * Registry-driven agent detail page. Financial-analysis sections read and
 * write their context through the route's search params so deep links and
 * cross-section navigation preserve period, granularity, and indicator.
 */
function StandardReviewAgentPage({
  jobId,
  meta,
  financialContext,
}: {
  jobId: string;
  meta: ReviewAgentEntry;
  financialContext: FinancialAnalysisContext;
}) {
  const navigate = useNavigate();
  const agentId = meta.dataAgentId;

  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );
  const isViewer = job?.userRole === "viewer";

  const { data: tasksData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTasksByJob,
      {
        jobId: jobId as Id<"jobs">,
      },
    ),
  );

  const task = useMemo(
    () =>
      (tasksData ?? []).find((t: { agentId: string }) => t.agentId === agentId),
    [tasksData, agentId],
  );

  const { mutateAsync: rerunAgent } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.summarization.actions.rerunSummarizationAgent,
    ),
  });

  const onBack = () => {
    navigate({
      to: "/dashboard/jobs/$jobId/review",
      params: { jobId },
    });
  };

  const handleRerun = async () => {
    if (!task) return;
    try {
      await rerunAgent({
        taskId: task._id as Id<"summarizationTasks">,
        jobId: jobId as Id<"jobs">,
      });
      toast.success("Agent re-running");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to re-run agent");
    }
  };

  // No task row yet — friendly placeholder until the first triggering
  // document indexes.
  if (!task) {
    const Icon = meta.icon;
    return (
      <div className="flex min-h-full flex-1 flex-col p-6">
        <div className="-m-6 flex flex-1 flex-col gap-5 bg-muted px-6 pb-6 pt-6">
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Investigations
            </Button>
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background">
              <Icon className="h-8 w-8 text-muted-foreground" />
            </span>
            <div className="max-w-xl text-center">
              <h2 className="text-lg font-medium">{meta.label}</h2>
              <p className="text-sm text-muted-foreground">
                Waiting for documents. This agent runs automatically once a
                document in one of its trigger categories is indexed.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="flex min-h-full flex-1 flex-col p-6">
        {/* Keyed by slug: the two financial-analysis destinations share an
            agent, and each must open on its own default tab (MOR-1280). The
            state a remount would lose lives in the search params instead. */}
        <AgentDetailRouter
          key={meta.slug}
          agentId={agentId}
          taskId={task._id as Id<"summarizationTasks">}
          jobId={jobId as Id<"jobs">}
          summarizationRunId={
            task.summarizationRunId as Id<"summarizationRuns">
          }
          onBack={onBack}
          onRerun={handleRerun}
          isViewer={isViewer}
          financialSection={meta.financialSection}
          financialContext={financialContext}
          onFinancialContextChange={(patch) =>
            navigate({
              to: "/dashboard/jobs/$jobId/review/$agent",
              params: { jobId, agent: meta.slug },
              search: { ...financialContext, ...patch },
              replace: true,
            })
          }
          onOpenFinancialSection={(section, context) =>
            navigate({
              to: "/dashboard/jobs/$jobId/review/$agent",
              params: {
                jobId,
                agent: reviewSlugForFinancialSection(section),
              },
              search: context,
            })
          }
        />
      </div>
    </div>
  );
}
